import React, { useState, useContext } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Bell,
  Users,
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  BookOpen,
  X,
  ExternalLink,
  Eye,
} from "lucide-react";
import {
  Notice,
  CreateNoticeRequest,
  getTeacherNotices,
  createNotice,
  deleteNotice,
  updateNotice,
  markNoticeAsRead,
  hideNotice,
} from "../../services/notices";
import { getTeacherClasses } from "../../services/classes";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

const TeacherAnnouncements: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [formData, setFormData] = useState<CreateNoticeRequest>({
    title: "",
    content: "",
    type: "academic",
    priority: "medium",
    targetAudience: ["students"],
    courseId: "",
    publishDate: new Date().toISOString().split("T")[0],
    expiryDate: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const queryClient = useQueryClient();

  // Check if current user can edit/delete a notice
  const canEditNotice = (notice: Notice): boolean => {
    if (!currentUser || !notice || !notice.author || !notice.author.userId)
      return false;

    // Handle both populated and non-populated author.userId
    const authorUserId =
      typeof notice.author.userId === "string"
        ? notice.author.userId
        : notice.author.userId &&
            typeof notice.author.userId === "object" &&
            notice.author.userId._id
          ? notice.author.userId._id
          : null;

    if (!authorUserId) return false;

    const currentUserId = String(currentUser.id);

    return String(authorUserId) === currentUserId;
  };

  // Get teacher's notices
  const { data: noticesResponse, isLoading } = useQuery({
    queryKey: ["teacher-notices", currentPage, itemsPerPage],
    queryFn: () =>
      getTeacherNotices({ page: currentPage, limit: itemsPerPage }),
  });

  // Get teacher's classes for context
  const { data: classesResponse } = useQuery({
    queryKey: ["teacher-classes"],
    queryFn: getTeacherClasses,
  });

  const notices = noticesResponse?.data || [];
  const pagination = noticesResponse?.pagination;
  const classes = classesResponse?.data || [];

  // Extract unique courses from teacher's classes
  const teacherCourses = React.useMemo(() => {
    const coursesMap = new Map();

    classes.forEach((classItem: any) => {
      if (classItem.courses && Array.isArray(classItem.courses)) {
        classItem.courses.forEach((course: any) => {
          if (course && course._id && !coursesMap.has(course._id)) {
            coursesMap.set(course._id, {
              _id: course._id,
              name: course.name,
              level: course.level,
              category: course.category,
            });
          }
        });
      }
    });

    return Array.from(coursesMap.values());
  }, [classes]);

  // Filter out notices with invalid data
  const validNotices = notices.filter(
    (notice: any) =>
      notice &&
      typeof notice === "object" &&
      notice._id &&
      notice.author &&
      typeof notice.author === "object" &&
      notice.author.userId,
  );

  // All notices returned are already filtered by the backend for teacher's role
  // This includes notices targeted at "all", "teachers", and "students"
  const teacherNotices = validNotices;

  // Create notice mutation
  const createNoticeMutation = useMutation({
    mutationFn: createNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-notices"] });
      setShowCreateModal(false);
      resetForm();
      toast.success("Announcement created successfully!");
    },
    onError: () => {
      toast.error("Failed to create announcement");
    },
  });

  // Update notice mutation
  const updateNoticeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateNoticeRequest }) =>
      updateNotice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-notices"] });
      setShowEditModal(false);
      setSelectedNotice(null);
      resetForm();
      toast.success("Announcement updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update announcement");
    },
  });

  // Delete notice mutation
  const deleteNoticeMutation = useMutation({
    mutationFn: deleteNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-notices"] });
      toast.success("Announcement deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete announcement");
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-notices"] });
    },
    onError: () => {
      toast.error("Failed to mark announcement as read");
    },
  });

  // Hide notice mutation
  const hideNoticeMutation = useMutation({
    mutationFn: hideNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-notices"] });
      queryClient.refetchQueries({ queryKey: ["teacher-notices"] });
      toast.success("Announcement hidden successfully");
    },
    onError: () => {
      toast.error("Failed to hide announcement");
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      type: "academic",
      priority: "medium",
      targetAudience: ["students"],
      courseId: "",
      publishDate: new Date().toISOString().split("T")[0],
      expiryDate: "",
    });
  };

  const handleCreate = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    createNoticeMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!selectedNotice || !formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    updateNoticeMutation.mutate({ id: selectedNotice._id, data: formData });
  };

  const handleViewNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setShowViewModal(true);
  };

  const handleEdit = (notice: Notice) => {
    setSelectedNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      type: notice.type,
      priority: notice.priority,
      targetAudience: notice.targetAudience,
      publishDate: notice.publishDate?.split("T")[0] || "",
      expiryDate: notice.expiryDate?.split("T")[0] || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (noticeId: string) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      deleteNoticeMutation.mutate(noticeId);
    }
  };

  const handleMarkAsRead = (noticeId: string) => {
    markAsReadMutation.mutate(noticeId);
  };

  const handleHideNotice = (noticeId: string) => {
    hideNoticeMutation.mutate(noticeId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "text-red-600 bg-red-50";
      case "important":
        return "text-orange-600 bg-orange-50";
      case "academic":
        return "text-blue-600 bg-blue-50";
      case "info":
        return "text-indigo-600 bg-indigo-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="p-4 sm:p-6 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Class Announcements
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Create announcements for your students and classes
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 border-0"
            >
              <Plus className="w-5 h-5" />
              Create Announcement
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">
                  My Classes
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {classes.length}
                </p>
              </div>
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">
                  Active Notices
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {pagination?.totalItems ||
                    teacherNotices.filter((n) => n.isActive).length}
                </p>
              </div>
              <Bell className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">
                  Total Notices
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {pagination?.totalItems || teacherNotices.length}
                </p>
              </div>
              <Megaphone className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Teacher's Classes Overview */}
        {classes.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-0 mb-6 sm:mb-8">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Your Classes
              </h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {classes.map((classItem: any) => (
                  <div
                    key={classItem._id}
                    className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100"
                  >
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
                      {classItem.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Students: {classItem.students?.length || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Notices List */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border-0">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              My Announcements
            </h2>
          </div>

          {isLoading ? (
            <div className="p-6 sm:p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Loading announcements...
              </p>
            </div>
          ) : teacherNotices.length === 0 ? (
            <div className="p-6 sm:p-8 text-center">
              <Bell className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                No announcements yet
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Create your first announcement for your students
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Announcement
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {teacherNotices.map((notice) => (
                <div
                  key={notice._id}
                  className={`p-4 sm:p-6 hover:bg-gray-50/50 transition-colors border-l-4 ${
                    !notice.isRead
                      ? "border-l-blue-500 bg-blue-50/30"
                      : "border-l-gray-300"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3
                          className={`text-base sm:text-lg font-semibold break-words ${
                            !notice.isRead ? "text-gray-900" : "text-gray-700"
                          }`}
                        >
                          {notice.title}
                        </h3>
                        {!notice.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        )}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getTypeColor(
                            notice.type,
                          )}`}
                        >
                          {notice.type}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getPriorityColor(
                            notice.priority,
                          )}`}
                        >
                          {notice.priority}
                        </span>
                        {!notice.isActive && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full text-gray-600 bg-gray-100 whitespace-nowrap">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div
                        onClick={() => handleViewNotice(notice)}
                        className="cursor-pointer hover:bg-gray-100/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                      >
                        <p className="text-sm sm:text-base text-gray-600 mb-1 line-clamp-2 break-words">
                          {notice.content}
                        </p>
                        <span className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Click to view full content
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-1 capitalize">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          {Array.isArray(notice.targetAudience)
                            ? notice.targetAudience.join(", ")
                            : notice.targetAudience}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          {new Date(notice.publishDate).toLocaleDateString()}
                        </div>
                        {notice.expiryDate && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                            Expires:{" "}
                            {new Date(notice.expiryDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:ml-4">
                      {!notice.isRead && !canEditNotice(notice) && (
                        <button
                          onClick={() => handleMarkAsRead(notice._id)}
                          disabled={markAsReadMutation.isPending}
                          className="w-full sm:w-auto px-3 py-2 bg-blue-600 text-white rounded-md text-xs sm:text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {markAsReadMutation.isPending
                            ? "Marking..."
                            : "Mark as Read"}
                        </button>
                      )}
                      <button
                        onClick={() => handleHideNotice(notice._id)}
                        disabled={hideNoticeMutation.isPending}
                        className="w-full sm:w-auto px-3 py-2 bg-gray-600 text-white rounded-md text-xs sm:text-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                      >
                        {hideNoticeMutation.isPending ? "Hiding..." : "Hide"}
                      </button>
                      {canEditNotice(notice) && (
                        <>
                          <button
                            onClick={() => handleEdit(notice)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit announcement"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(notice._id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete announcement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {!canEditNotice(notice) && notice.isRead && (
                        <div className="text-xs text-gray-400 px-2 py-1 break-words">
                          Created by {notice.author?.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {teacherNotices.length > 0 && pagination && (
            <div className="px-6 py-4 border-t border-gray-200/60 bg-gradient-to-r from-gray-50 to-blue-50">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={setCurrentPage}
                pageSize={itemsPerPage}
                onPageSizeChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
                showPageSizeSelector={true}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                Create Class Announcement
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="info">Information</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: "students", label: "My Students" },
                      { value: "parents", label: "Parents" },
                    ].map((audience) => (
                      <label
                        key={audience.value}
                        className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors border border-gray-200"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.targetAudience?.includes(
                              audience.value as any,
                            ) || false
                          }
                          onChange={(e) => {
                            const currentAudiences =
                              formData.targetAudience || [];
                            let newAudiences: typeof formData.targetAudience;

                            if (e.target.checked) {
                              newAudiences = [
                                ...currentAudiences,
                                audience.value as any,
                              ];
                            } else {
                              newAudiences = currentAudiences.filter(
                                (a) => a !== audience.value,
                              );
                              // Ensure at least one audience is selected
                              if (newAudiences.length === 0) {
                                newAudiences = ["students"];
                              }
                            }

                            setFormData({
                              ...formData,
                              targetAudience: newAudiences,
                            });
                          }}
                          className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="text-gray-700 font-medium text-sm">
                          {audience.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course (Optional - leave empty for all courses)
                </label>
                <select
                  value={formData.courseId || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      courseId: e.target.value || undefined,
                    })
                  }
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Courses</option>
                  {teacherCourses.map((course: any) => (
                    <option key={course._id} value={course._id}>
                      {course.name} ({course.level} - {course.category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createNoticeMutation.isPending}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 order-1 sm:order-2"
              >
                {createNoticeMutation.isPending
                  ? "Creating..."
                  : "Create Announcement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                Edit Class Announcement
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="info">Information</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: "students", label: "My Students" },
                      { value: "parents", label: "Parents" },
                    ].map((audience) => (
                      <label
                        key={audience.value}
                        className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors border border-gray-200"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.targetAudience?.includes(
                              audience.value as any,
                            ) || false
                          }
                          onChange={(e) => {
                            const currentAudiences =
                              formData.targetAudience || [];
                            let newAudiences: typeof formData.targetAudience;

                            if (e.target.checked) {
                              newAudiences = [
                                ...currentAudiences,
                                audience.value as any,
                              ];
                            } else {
                              newAudiences = currentAudiences.filter(
                                (a) => a !== audience.value,
                              );
                              // Ensure at least one audience is selected
                              if (newAudiences.length === 0) {
                                newAudiences = ["students"];
                              }
                            }

                            setFormData({
                              ...formData,
                              targetAudience: newAudiences,
                            });
                          }}
                          className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="text-gray-700 font-medium text-sm">
                          {audience.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateNoticeMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {updateNoticeMutation.isPending
                  ? "Updating..."
                  : "Update Announcement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Announcement Modal */}
      {showViewModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Announcement Details
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedNotice.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${getTypeColor(
                      selectedNotice.type,
                    )}`}
                  >
                    {selectedNotice.type}
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${getPriorityColor(
                      selectedNotice.priority,
                    )}`}
                  >
                    {selectedNotice.priority}
                  </span>
                  {!selectedNotice.isActive && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full text-gray-600 bg-gray-100">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                  Content
                </h4>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedNotice.content}
                </p>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Target Audience
                    </span>
                  </div>
                  <p className="text-gray-900 font-semibold capitalize">
                    {Array.isArray(selectedNotice.targetAudience)
                      ? selectedNotice.targetAudience.join(", ")
                      : selectedNotice.targetAudience}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Published Date
                    </span>
                  </div>
                  <p className="text-gray-900 font-semibold">
                    {new Date(selectedNotice.publishDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>

                {selectedNotice.expiryDate && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Expiry Date
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {new Date(selectedNotice.expiryDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )}
                    </p>
                  </div>
                )}

                {selectedNotice.author && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Author
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {selectedNotice.author.name}
                    </p>
                    <p className="text-gray-600 text-sm capitalize">
                      {selectedNotice.author.department}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAnnouncements;
